import {
  Component,
  ChangeDetectionStrategy,
  DestroyRef,
  inject,
  input,
  model,
  output,
  signal,
  computed,
  effect,
} from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMessageService } from 'ng-zorro-antd/message';
import {
  SaldoVacaciones,
  SolicitudVacaciones,
  UpdateSolicitudRequest,
} from '@schemas/vacaciones.schema';
import { DiaFestivo } from '@schemas/festivos.schema';
import { VacacionesService } from '@services/vacaciones.service';
import { FestivosService } from '@services/festivos.service';
import {
  calcularDiasHabiles,
  calcularDistribucionFIFO,
  commitRangeOnEnter,
  parseLocalDate,
  toISODateString,
} from '@shared/vacaciones.utils';

import { PageHeader, SectionHeader } from '@intelishs-group/ui';

@Component({
  selector: 'app-solicitud-editar-modal',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe,
    DecimalPipe,
    FormsModule,
    NzButtonModule,
    NzModalModule,
    NzDatePickerModule,
    NzInputModule,
    NzIconModule,
    PageHeader,
    SectionHeader,
  ],
  templateUrl: './solicitud-editar-modal.html',
  styleUrl: './solicitud-editar-modal.css',
})
export class SolicitudEditarModal {
  private readonly vacacionesService = inject(VacacionesService);
  private readonly festivosService = inject(FestivosService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly message = inject(NzMessageService);

  visible = model(false);

  /**
   * El contexto va en el SUBTÍTULO, nunca en el título, y se arma aquí porque
   * `page-header` recibe un string: con partes opcionales, el encadenado se
   * lee peor en el template.
   */
  subtitulo = computed(() => {
    const sol = this.solicitud();
    return sol ? `${sol.empleadoIdInterno} · ${sol.empleadoNombre}` : '';
  });
  solicitud = input<SolicitudVacaciones | null>(null);
  /**
   * Saldo del empleado SIN esta solicitud — alimenta el desglose por ciclo.
   *
   * Lo pide el propio modal en vez de recibir el de la página: aquel ya trae
   * descontados los días de la solicitud que se edita (las pendientes
   * consumen), así que si ella agotó el ciclo anterior, el desglose lo veía
   * vacío y decía que sus días salían del ciclo actual.
   */
  saldo = signal<SaldoVacaciones | null>(null);
  saved = output<void>();

  editDateRange = signal<Date[] | null>(null);
  editComentarios = signal('');
  editSubmitting = signal(false);

  editDiasSolicitados = computed(() => {
    const range = this.editDateRange();
    if (!range || range.length !== 2) return 0;
    return calcularDiasHabiles(range[0], range[1], this.festivosService.festivos());
  });

  /**
   * Mismo desglose que el alta. Cambiar las fechas de una solicitud mueve de
   * qué ciclo salen los días igual que crearla, así que el empleado necesita
   * verlo en los dos lados.
   */
  distribucionFIFO = computed(() =>
    calcularDistribucionFIFO(this.saldo(), this.editDateRange(), this.festivosService.festivos()),
  );

  /** Mismo bloqueo que el alta: el backend rechaza la edición si excede el saldo. */
  excedeSaldo = computed(() => (this.distribucionFIFO()?.excedente ?? 0) > 0);

  festivosEnRango = computed((): DiaFestivo[] => {
    const range = this.editDateRange();
    if (!range || range.length !== 2) return [];
    const [inicio, fin] = range;
    return this.festivosService
      .festivos()
      .filter((f) => f.fecha >= toISODateString(inicio) && f.fecha <= toISODateString(fin));
  });

  /**
   * "3 días festivos" / "1 día festivo". Los plurales se resuelven aquí y no con
   * un "día(s) festivo(s)" en la plantilla: eso es una cuenta a medio hacer, y
   * el caso de uno solo es el más común en un puente.
   */
  textoFestivos(): string {
    const n = this.festivosEnRango().length;
    return n === 1 ? '1 día festivo' : `${n} días festivos`;
  }

  festivosEnRangoNombres = computed(() =>
    this.festivosEnRango()
      .map((f) => f.nombre)
      .join(', '),
  );

  /** Fecha mínima permitida: hoy + 14 días naturales. */
  readonly fechaMinimaSolicitud = (() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 14);
    return d;
  })();

  editFechaInicioInvalida = computed(() => {
    const range = this.editDateRange();
    if (!range || range.length < 1 || !range[0]) return false;
    const inicio = new Date(range[0]);
    inicio.setHours(0, 0, 0, 0);
    return inicio < this.fechaMinimaSolicitud;
  });

  /** Deshabilita pasadas, fines de semana y festivos. Las próximas 2 semanas se permiten con advertencia. */
  disabledDate = (current: Date): boolean => {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const dow = current.getDay();
    return current < hoy || dow === 0 || dow === 6 || this.festivosService.esFestivo(current);
  };

  /** True para días hábiles dentro de las próximas 2 semanas (zona de advertencia). Los fines de semana quedan excluidos — ya están deshabilitados en gris. */
  esDentroDeDosSemanas = (current: Date): boolean => {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const c = new Date(current);
    c.setHours(0, 0, 0, 0);
    const dow = c.getDay();
    if (dow === 0 || dow === 6) return false;
    return c >= hoy && c < this.fechaMinimaSolicitud;
  };

  readonly onEditRangeChange = (dates: Date[] | null): void => this.editDateRange.set(dates);

  commitRangeOnEnter = commitRangeOnEnter;

  constructor() {
    // Cuando el modal se abre con una solicitud, precargar el estado del form.
    effect(() => {
      if (this.visible()) {
        const sol = this.solicitud();
        if (sol) {
          this.editDateRange.set([parseLocalDate(sol.fechaInicio), parseLocalDate(sol.fechaFin)]);
          this.editComentarios.set(sol.comentarios ?? '');
          this.cargarSaldo(sol);
        } else {
          this.editDateRange.set(null);
          this.editComentarios.set('');
        }
      }
    });
  }

  private cargarSaldo(sol: SolicitudVacaciones): void {
    this.saldo.set(null);
    this.vacacionesService
      .getSaldo(sol.empleadoId, sol.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: (s) => this.saldo.set(s) });
  }

  cerrar(): void {
    this.visible.set(false);
  }

  submit(): void {
    const sol = this.solicitud();
    const range = this.editDateRange();
    if (!sol || !range || range.length !== 2 || this.excedeSaldo()) return;

    // Advertencia informativa — no bloquea el guardado.
    if (this.editFechaInicioInvalida()) {
      this.message.warning(
        'Las vacaciones deben solicitarse con al menos 2 semanas de anticipación. ' +
          'La solicitud se actualizará pero quedará marcada como fuera de política.',
        { nzDuration: 6000 },
      );
    }

    this.editSubmitting.set(true);
    const request: UpdateSolicitudRequest = {
      fechaInicio: toISODateString(range[0]),
      fechaFin: toISODateString(range[1]),
      comentarios: this.editComentarios() || undefined,
    };

    this.vacacionesService.update(sol.id, request).subscribe({
      next: () => {
        this.message.success('Solicitud actualizada correctamente');
        this.editSubmitting.set(false);
        this.saved.emit();
        this.cerrar();
      },
      error: (err) => {
        const msg = err?.error?.errorMessage ?? 'Error al actualizar la solicitud';
        this.message.error(msg);
        this.editSubmitting.set(false);
      },
    });
  }
}
